import { Breadcrumbs, Header, LoadingState, View, Workspace } from "components/Shared";
import { translate } from "lib";
import Datasource, { DatasourceResponse } from "lib/datasource";
import _ from "lodash";
import React, { memo, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import AmendmentApiCallsTab from "./AmendmentApiCallsTab";
import AmendmentAssetsTab from "./AmendmentAssetsTab";
import AmendmentDetailsTab from "./AmendmentDetailsTab";
import { useFeatureFlagGuard } from "v2/hooks";

/**
 * ONE AMENDMENT (GET /v2/amendments/:id) — reached by the View button on the
 * lease's Amendments tab.
 *
 * ---------------------------------------------------------------------------
 * IT IS A VIEW WITH THREE TABS (Sim, 26 + 27 Aug)
 * ---------------------------------------------------------------------------
 *   Details   what Yardi says about the amendment, as a definition list, with an
 *             Edit button at the bottom for the fields a human may correct. The
 *             form itself is its OWN page (AmendmentEdit) — exactly the
 *             relationship the lease's Details tab has with LeaseAgreementEdit
 *             (Sim, 26 Aug: "should only show details just like the
 *             LeaseAgreement Detail, then at the bottom with Edit button").
 *   Assets    `Assets[]` from the payload that named this AmendmentID. READ-ONLY,
 *             and with no Edit button: /lease_data is the only writer of an Asset.
 *   API Call  every inbound /lease_data call that wrote this amendment, and what
 *             TCP answered back. It was a tab on the PROJECT workspace until
 *             27 Aug 2026; Sim moved it here with the association behind it,
 *             because a payload row is about an AmendmentID and only some types
 *             go on to create a project at all.
 *
 * That is why the button on the lease's Amendments list says View and not Edit:
 * this page STATES the amendment, and correcting it is one step further in.
 *
 * DETAILS IS FIRST AND THEREFORE THE DEFAULT — `Tabs` selects the first tab it is
 * given, or whatever the URL hash names. The amendment's own fields are what
 * people come here for; the assets and the call history are reference material.
 * AmendmentEdit returns
 * to `#details` on save, which is that hash.
 *
 * The record is fetched HERE, once, and handed to the Details tab: it is the
 * page's title as well as the tab's whole content, so fetching it twice would be
 * a second request for something already on screen. The Assets tab fetches its
 * own rows, the way every other tab in this folder does.
 */

const AmendmentShow = () => {
	const { id, amendmentId } = useParams<{ id: string; amendmentId: string }>();
	// null = still loading; false = the API refused, or the record is gone.
	const [amendment, setAmendment] = useState<tcpinpoint.Amendment | null | false>(null);
	const [datasource] = useState(new Datasource({ mainModelName: "amendment" }));

	useEffect(() => {
		let sub = datasource.responseSubject$.subscribe((response: DatasourceResponse) =>
			setAmendment(response.success ? response.normalizedMainModelResponse || false : false)
		);
		return () => sub.unsubscribe();
	}, [datasource]);

	useEffect(() => {
		if (!_.isEmpty(amendmentId)) datasource.get("v2", `amendments/${amendmentId}`, {});
	}, [datasource, amendmentId]);

	// BEHIND THE `lease-agreements` FLAG (Sim, 22 Sep 2026). Hiding the masthead
	// entry is not access control — a typed URL or an old bookmark still lands
	// here, and without this the screen mounted and turned the API's 403 into
	// "This lease is not available", which reads like missing data rather than
	// "not yours". The API keeps its own gate; this is the UI half.
	//
	// BEFORE the record checks below, so a user without the flag is redirected
	// rather than shown the not-found state their own 403 produced.
	const allowed = useFeatureFlagGuard("lease-agreements");
	if (!allowed) return null;

	if (amendment === null) return <LoadingState />;

	if (amendment === false)
		return (
			<React.Fragment>
				<Breadcrumbs items={[{ title: "lease_agreements", link: "/lease-agreements" }]} />
				<Header title="amendment" stamp="lease_agreements" />
				<Workspace id="amendment">
					<View resource="amendment">
						<div className="alert alert-light empty-state">{translate("amendment_not_found")}</div>
					</View>
				</Workspace>
			</React.Fragment>
		);

	const leaseTitle = amendment.lease_agreement_tcode || (translate("lease_agreement") as string);
	const title = `${amendment.amendment_type || translate("amendment")} #${amendment.amendment_id}`;

	return (
		<React.Fragment>
			<Breadcrumbs
				items={[
					{ title: "lease_agreements", link: "/lease-agreements" },
					{ title: leaseTitle, link: `/lease-agreements/${id}` },
					{ title }
				]}
			/>
			<Header title={title} subtitle={leaseTitle} stamp="lease_agreements" />
			<Workspace
				id="amendment"
				tabs={[
					// FIRST = DEFAULT. Do not reorder without meaning to change which tab
					// the page opens on.
					<AmendmentDetailsTab
						name="details"
						key="details-tab"
						amendment={amendment}
						leaseAgreementId={id}
					/>,
					<AmendmentAssetsTab name="assets" key="assets-tab" amendmentId={amendment.id} />,
					<AmendmentApiCallsTab
						name="api_calls"
						key="api-calls-tab"
						path={`amendments/${amendment.id}/api-calls`}
					/>
				]}
			/>
		</React.Fragment>
	);
};

export default memo(AmendmentShow);
